import React, { useState, useCallback, useEffect } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { DatasetBrowser } from '@/components/evaluator/DatasetBrowser';
import { DatasetItemsList } from '@/components/evaluator/DatasetItemsList';
import { DatasetItemDetail } from '@/components/evaluator/DatasetItemDetail';

export const EvaluatorContent: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const isMobile = useIsMobile();

  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'sidebar' | 'center'>('sidebar');

  useEffect(() => {
    if (!isMobile) setMobileView('sidebar');
  }, [isMobile]);

  const handleSelectDataset = useCallback((name: string) => {
    setSelectedDataset(name);
    setSelectedItemId(null);
    if (isMobile) setMobileView('center');
  }, [isMobile]);

  const handleSelectItem = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
  }, []);

  const handleBackToItems = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  const handleDatasetDeleted = useCallback(() => {
    setSelectedDataset(null);
    setSelectedItemId(null);
  }, []);

  const renderCenter = () => {
    if (selectedDataset && selectedItemId && orgId) {
      return (
        <div className="flex-1 overflow-auto p-6">
          <DatasetItemDetail
            orgId={orgId}
            datasetName={selectedDataset}
            itemId={selectedItemId}
            onBack={handleBackToItems}
          />
        </div>
      );
    }

    if (selectedDataset && orgId) {
      return (
        <DatasetItemsList
          orgId={orgId}
          datasetName={selectedDataset}
          onSelectItem={handleSelectItem}
        />
      );
    }

    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a dataset to view items
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-13rem)] rounded-xl border card-border card-bg overflow-hidden">
      {/* Left sidebar - Datasets */}
      {(!isMobile || mobileView === 'sidebar') && (
        <DatasetBrowser
          orgId={orgId}
          selectedDataset={selectedDataset}
          onSelectDataset={handleSelectDataset}
          onDatasetDeleted={handleDatasetDeleted}
        />
      )}

      {/* Center panel - Items / Item detail */}
      {(!isMobile || mobileView === 'center') && (
        <div className="flex-1 flex flex-col card-bg overflow-hidden">
          {isMobile && (
            <div className="border-b card-border px-4 py-2 flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMobileView('sidebar')}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <span className="text-sm text-muted-foreground">Dataset items</span>
            </div>
          )}
          {renderCenter()}
        </div>
      )}
    </div>
  );
};
